import * as React from 'react'
import { connect } from 'react-redux'
import { Dispatch, compose } from 'redux'
import * as R from 'ramda'

import { Store } from '../../../reducers/index'
import { Action } from '../../../actions/types'
import { uiWorking, uiWorkDone } from '../../../actions/ui_actions'
import { setJobId, clearJobId } from '../../../actions/calculation'
import { getJobId } from '../../../reducers/calculation'

import {
  getWithQueueJobMutation,
  WrappedProps,
  SlabInput
} from './calculationQueries'

import { recordEvent, recordException } from '../../../analytics/index'
import {
  getActiveCase,
  EditableCase,
  getProjectLocation,
  EditableSlab,
  // EditableLoadCase,
  getAllTouchedAndValid,
  getIsNewProject,
  getProjectId
} from '../../../reducers/project'
import { resetProjectDirty } from '../../../actions/project'
import SubmitButton from './SubmitButton'
import Error from '../../Shared/Alerts/Error'

type InputProps = {
  startWorking: () => Action
  finishWorking: () => Action
  setJobId: (jobId: string) => Action
  clearJobId: () => Action
  resetDirty: () => Action

  working: boolean
  allValid: boolean
  isNew: boolean

  jobId: string | undefined

  case: EditableCase | undefined
  location: string
  projectId: string | undefined
}

type Props = InputProps & WrappedProps

type State = {
  error: string | undefined
}

class CalcWrapper extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { error: undefined }

    this.dismissError = this.dismissError.bind(this)
  }

  dismissError() {
    this.setState({ error: undefined })
  }

  render() {
    const { jobId, projectId, case: kase } = this.props

    if (!projectId || !kase) {
      return null
    }

    return (
      <div>
        {this.state.error && (
          <Error onDismiss={this.dismissError}>{this.state.error}</Error>
        )}
        <SubmitButton
          caseId={kase.id}
          projectId={projectId}
          jobId={jobId}
          handleSubmit={this.handleSubmit}
          allValid={this.props.allValid}
        />
      </div>
    )
  }

  private handleSubmit = async () => {
    try {
      const { location } = this.props
      const kase = this.props.case

      if (!kase) {
        return
      }
      this.props.startWorking()
      this.props.clearJobId()

      const leftSlab: EditableSlab | undefined = R.find(
        R.propEq('name', 'left'),
        kase.slabs
      )
      const rightSlab: EditableSlab | undefined = R.find(
        R.propEq('name', 'right'),
        kase.slabs
      )
      const beam = kase.beams[0]

      if (!beam || !leftSlab || !rightSlab) {
        return
      }

      /* If this is not a saved project, don't pass case ID (because the case doesn't
         exist yet on the server-side). */
      const caseId = this.props.isNew ? null : kase.id

      // All slabs must share top concrete value for now. See updateSlab in projects
      // reducer
      const topConcrete = leftSlab.topConcrete || 0

      const slabs = R.reject(R.propEq('disabled', true), kase.slabs).map<
        SlabInput
      >(slab => ({
        name: slab.name,
        model: slab.model,
        pos: 0,
        side: slab.name === 'left' ? 'left' : 'right',
        type: slab.type,
        span: slab.span,
        weight: slab.weight,
        topConcrete
      }))

      const { data } = await this.props.queueJob(
        {
          location: location,
          spaceType: kase.spaceType,
          elements: {
            slabs,
            beams: [
              {
                type: beam.type,
                concreteGrade: beam.concreteGrade,
                span: beam.length,
                pos: 0,
                profileType: beam.profileType || 'D',
                name: 'beam1',
                topConcrete,
                fireResistance: beam.fireResistance
              }
            ],
            loadCases: kase.loadCases
              // Skip load cases marked with "ignore" (these are ones we display for the user's convenience
              // but are taken into account in the calculations without our input
              .filter(loadCase => !loadCase.ignore)
              .map(loadCase => ({
                name: loadCase.name,
                action: loadCase.action,
                stage: loadCase.stage,
                loads: loadCase.loads
              }))
          }
        },
        caseId
      )

      recordEvent('calculation', 'calculation_started')
      this.props.resetDirty()
      console.log('Queued job with ID', data.queueJob.jobId)
      this.props.setJobId(data.queueJob.jobId)
    } catch (e) {
      console.error('Failed to queue job', e.message)
      recordException('calculation_start_failed')
      this.props.finishWorking()
      this.setState({ error: e.message })
    }
  }
}

const mapStateToProps = (state: Store.Complete) => ({
  projectId: getProjectId(state),
  working: state.ui.working,
  allValid: getAllTouchedAndValid(state),
  jobId: getJobId(state),
  case: getActiveCase(state),
  isNew: getIsNewProject(state),
  location: getProjectLocation(state)
})

const mapDispatchToProps = (dispatch: Dispatch<Store.Complete>) => ({
  startWorking: () => dispatch(uiWorking()),
  finishWorking: () => dispatch(uiWorkDone()),
  setJobId: (jobId: string) => dispatch(setJobId(jobId)),
  clearJobId: () => dispatch(clearJobId()),
  resetDirty: () => dispatch(resetProjectDirty())
})

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  getWithQueueJobMutation<InputProps>()
)(CalcWrapper)



// WEBPACK FOOTER //
// ./src/Components/Workspace/Calculation/index.tsx